import { useCallback, useRef, useState, type DragEvent } from 'react';

/**
 * Соответствие файла фильтру `accept` (как у <input accept>): список через запятую
 * из mime ('application/pdf'), wildcard ('image/*') и расширений ('.jpg'). Пусто → любой.
 */
export function fileMatchesAccept(file: File, accept?: string): boolean {
  const trimmed = accept?.trim();
  if (!trimmed) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return trimmed.split(',').some(raw => {
    const token = raw.trim().toLowerCase();
    if (!token) return false;
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

interface UseFileDropOptions {
  /** Вызывается с отфильтрованными (по accept/multiple) файлами при дропе. */
  onFiles: (files: File[]) => void;
  /** Фильтр типов (как в <input accept>); не задан — принимаются любые. */
  accept?: string;
  /** Разрешён ли выбор нескольких: false — берётся только первый файл. */
  multiple?: boolean;
  /** Отключить приём (напр. идёт загрузка или запрещён upload). */
  disabled?: boolean;
}

/**
 * Нативный drag-n-drop файлов без dnd-библиотек. Возвращает флаг подсветки
 * `dragActive` и `dropHandlers` для контейнера. Считает глубину вложенных
 * dragenter/leave, чтобы подсветка не мигала на дочерних элементах; фильтрует
 * дропнутые файлы по `accept` и режет до одного при `multiple=false`.
 */
export function useFileDrop({ onFiles, accept, multiple = false, disabled = false }: UseFileDropOptions) {
  const [dragActive, setDragActive] = useState(false);
  const depth = useRef(0);

  const hasFiles = (e: DragEvent<HTMLElement>) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const onDragEnter = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragActive(true);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (_e: DragEvent<HTMLElement>) => {
      if (disabled) return;
      depth.current -= 1;
      if (depth.current <= 0) {
        depth.current = 0;
        setDragActive(false);
      }
    },
    [disabled]
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (disabled) return;
      e.preventDefault();
      depth.current = 0;
      setDragActive(false);
      const dropped = Array.from(e.dataTransfer?.files ?? []).filter(f => fileMatchesAccept(f, accept));
      if (dropped.length === 0) return;
      onFiles(multiple ? dropped : dropped.slice(0, 1));
    },
    [disabled, accept, multiple, onFiles]
  );

  return { dragActive, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
